/**
 * The public frames (blueprint §5.2 `public`, mockup carbon/04, DESIGN.md
 * "Sign in (04)"). Nothing here carries the app shell: no header bar on the
 * shell color and no side nav.
 *
 *   PublicLayout (default)  The /docs layout route: a 48px brand bar with the
 *                           Documentation link and Sign in, the page, then a
 *                           legal row.
 *   SignInFrame             The 04 page: a 560px column (the brand row, a
 *                           vertically centred 432px body with the h1, the
 *                           lede, a rule and the caller's content, then the
 *                           legal row) beside the art panel, where the three
 *                           pillars sit as full-height condensed-grid rows with
 *                           20px swatches; their content rises in on load, one
 *                           row after another (the page's one load moment).
 *                           Below 1056px the art drops under
 *                           the column. Sign-in, the onboarding status, sign-up
 *                           and both consent callbacks use it, so the public
 *                           surface reads as one page.
 *   BrandBar, FrameNote     The 48px brand bar (docs, the setup wizard) and
 *                           the bordered note at the foot of the 04 body
 *                           ("New to Guardian? Request access …").
 *
 * Tokens carry both themes. The logo swaps with the `.light` class, so these
 * frames need no theme context and render in SSR tests. The legal row is
 * "© {year} iuvo" only: no Privacy, Terms or Status page exists yet (§7 N7,
 * D32), and the year is read at runtime (N8).
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Link, Outlet } from 'react-router-dom'
import clsx from 'clsx'
import { ButtonLink, CondensedGrid, Swatch, type Category } from '@/components/carbon'

/** "© 2026 iuvo", with the year read at runtime. */
function Legal({ className }: { className?: string }) {
  return <p className={clsx('m-0 text-helper text-text-helper', className)}>&copy; {new Date().getFullYear()} iuvo</p>
}

/** The iuvo wordmark and "Guardian" at 600 (the 04 brand row). */
function BrandRow({ to }: { to: string }) {
  return (
    <Link
      to={to}
      className="flex w-fit min-w-0 flex-none items-center gap-2 whitespace-nowrap text-body-lg font-semibold tracking-[0.1px] text-text-primary"
    >
      <img src="/iuvo-logo-on-dark.png" alt="iuvo" width={70} height={20} className="block h-5 w-auto flex-none light:hidden" />
      <img src="/iuvo-logo-on-light.png" alt="iuvo" width={70} height={20} className="hidden h-5 w-auto flex-none light:block" />
      <span>Guardian</span>
    </Link>
  )
}

export interface BrandBarProps {
  /** Where the wordmark links. */
  to: string
  /** Right-aligned links or buttons. */
  children?: ReactNode
}

/** A 48px full-bleed bar on the ground, like the shell header: the wordmark, a fill, then the caller's links. Theme-following, not the shell. */
export function BrandBar({ to, children }: BrandBarProps) {
  return (
    <header className="sticky top-0 z-10 border-b border-line-subtle bg-[color:var(--background)]">
      <div className="flex h-12 items-center gap-6 px-8 max-c-md:gap-4 max-c-md:px-4">
        <BrandRow to={to} />
        <span className="min-w-0 flex-1" />
        {children}
      </div>
    </header>
  )
}

/** The bordered line at the foot of the 04 body: secondary text over a border-subtle rule. */
export function FrameNote({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={clsx('mt-8 flex flex-col gap-2 border-t border-line-subtle pt-6 text-body text-text-secondary', className)}>
      {children}
    </div>
  )
}

/** A link in the public frames' copy: link color, underlined on hover. */
export const PUBLIC_LINK = 'text-link hover:text-link-hover hover:underline'

const PILLARS: ReadonlyArray<{ category: Category; name: string; line: string }> = [
  // §7 N2 / D33: "your baseline". Nothing records an approval, so the copy never says "the baseline you approved".
  { category: 'baseline', name: 'Baseline Compliance', line: 'Every setting checked against your baseline.' },
  { category: 'bpa', name: 'Best Practice', line: 'Your tenant measured against CISA SCuBA, NIST 800-53 and Microsoft MCSB.' },
  { category: 'identity', name: 'Identity', line: 'MFA and sign-in hygiene for every member.' },
]

/**
 * The page's one load moment (DESIGN.md "Motion"): each pillar row's content
 * rises 16px and fades in, 110ms apart. It starts two frames after mount, as
 * the mockup does, and prefers-reduced-motion shows the rows in place.
 */
function useArrived() {
  const [arrived, setArrived] = useState(false)
  useEffect(() => {
    let second = 0
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setArrived(true))
    })
    return () => {
      cancelAnimationFrame(first)
      cancelAnimationFrame(second)
    }
  }, [])
  return arrived
}

const ARRIVE = 'transition-[transform,opacity] duration-400 ease-carbon-in motion-reduce:transition-none'
const BEFORE_ARRIVAL = 'translate-y-4 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100'

/** The 04 art panel: the three pillars as full-height condensed-grid rows, each led by its 20px swatch. */
function PillarArt() {
  const arrived = useArrived()
  const motion = clsx(ARRIVE, !arrived && BEFORE_ARRIVAL)
  return (
    <section
      aria-label="What Guardian checks"
      className="min-w-0 px-8 pb-8 max-c-md:px-4 max-c-md:pb-4 c-lg:col-start-2 c-lg:row-span-2 c-lg:row-start-1 c-lg:grid c-lg:bg-layer-1 c-lg:p-0"
    >
      <CondensedGrid columns={{ base: 1 }} className="c-lg:grid-rows-3 c-lg:border-0">
        {PILLARS.map((p, i) => (
          <article
            key={p.category}
            className="flex min-w-0 flex-col justify-end bg-layer-1 p-8 max-c-md:px-4 max-c-md:pb-5 max-c-md:pt-4 c-lg:p-12"
          >
            <h2
              className={clsx(
                'm-0 flex items-center gap-4 text-[1.75rem] font-light leading-[1.25] text-text-primary max-c-md:gap-3 max-c-md:text-[1.125rem] max-c-md:font-normal max-c-md:leading-[1.4] c-lg:text-figure',
                motion,
              )}
              style={{ transitionDelay: `${i * 110}ms` }}
            >
              <Swatch category={p.category} size={20} className="max-c-md:h-4 max-c-md:w-4" />
              {p.name}
            </h2>
            <p
              className={clsx(
                'm-0 ml-9 mt-2 max-w-[44ch] text-body-lg text-text-secondary max-c-md:ml-7 max-c-md:mt-1 max-c-md:text-body',
                motion,
              )}
              style={{ transitionDelay: `${i * 110}ms` }}
            >
              {p.line}
            </p>
          </article>
        ))}
      </CondensedGrid>
    </section>
  )
}

export interface SignInFrameProps {
  /** The page's one h1 (Headline Small: 2.625rem/300, 2rem on phones). */
  headline: ReactNode
  /** The lede under it, 1.125rem/300 in secondary text. */
  lede?: ReactNode
  /** Below the rule: the Microsoft button, notifications, forms, next steps. */
  children?: ReactNode
  /** Where the wordmark links. */
  brandTo?: string
  /** id of the h1, for an aria-labelledby. */
  headingId?: string
}

/** The 04 sign-in page frame. Renders its own <main>: these pages sit outside Layout. */
export function SignInFrame({ headline, lede, children, brandTo = '/', headingId }: SignInFrameProps) {
  return (
    <main
      id="main"
      className="grid min-h-screen grid-cols-1 content-start c-lg:grid-cols-[minmax(0,560px)_minmax(0,1fr)] c-lg:grid-rows-[minmax(0,1fr)_auto] c-lg:content-stretch"
    >
      <div className="flex min-w-0 flex-col px-8 pt-8 max-c-md:px-4 max-c-md:pt-6 c-lg:col-start-1 c-lg:row-start-1 c-lg:border-r c-lg:border-line-subtle">
        <BrandRow to={brandTo} />
        <div className="my-auto w-full max-w-[432px] py-12 max-c-md:pb-8 max-c-md:pt-10">
          <h1
            id={headingId}
            className="m-0 text-balance text-headline-sm font-light tracking-normal text-text-primary max-c-md:text-[2rem]"
          >
            {headline}
          </h1>
          {lede ? <p className="m-0 mt-4 max-w-[56ch] text-[1.125rem] font-light leading-[1.5] text-text-secondary">{lede}</p> : null}
          {children ? (
            <>
              <hr className="my-8 border-0 border-t border-line-subtle" />
              {children}
            </>
          ) : null}
        </div>
      </div>
      <PillarArt />
      <Legal className="px-8 pb-8 pt-6 max-c-md:px-4 max-c-md:pb-6 max-c-md:pt-4 c-lg:col-start-1 c-lg:row-start-2 c-lg:border-r c-lg:border-line-subtle" />
    </main>
  )
}

/** The /docs layout route: the brand bar, the page, the legal row. */
export default function PublicLayout() {
  return (
    <div className="flex min-h-screen flex-col">
      <BrandBar to="/docs">
        <nav aria-label="Documentation" className="flex flex-none items-center gap-6">
          <Link to="/docs" className={clsx('text-body max-c-md:hidden', PUBLIC_LINK)}>
            Documentation
          </Link>
          <ButtonLink to="/dashboard" kind="tertiary" size="sm">
            Sign in
          </ButtonLink>
        </nav>
      </BrandBar>
      <main id="main" className="min-w-0 flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-line-subtle">
        <div className="mx-auto flex max-w-[1584px] flex-wrap items-center justify-between gap-x-6 gap-y-2 px-8 py-6 max-c-md:px-4">
          <Legal />
          <a href="mailto:support@iuvotech.com" className={clsx('text-helper', PUBLIC_LINK)}>
            Contact support
          </a>
        </div>
      </footer>
    </div>
  )
}
